import React, { useState, useRef, useEffect } from 'react';
import { View, Text, StyleSheet, Image, TouchableOpacity, Platform, Animated, ScrollView } from 'react-native';
import { useTheme } from '../context/ThemeContext';
import { useNavigation } from '../context/NavigationContext';
import FormInput from '../components/FormInput';
import PrimaryButton from '../components/PrimaryButton';
import { authService } from '../services/authService';

// Import character images
const characterWaving = require('../assets/character_stills/FearIsFuel_CharacterStills_Tommy_Waving_v01_SL.png');

const ComingSoonScreen = () => {
  const { palette, mode } = useTheme();
  const { navigateTo } = useNavigation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  
  // Admin login state
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [adminLoginLoading, setAdminLoginLoading] = useState(false);
  const [adminLoginError, setAdminLoginError] = useState('');

  // Animations
  const cardScale = useRef(new Animated.Value(0.9)).current;
  const cardOpacity = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.parallel([
      Animated.spring(cardScale, {
        toValue: 1,
        tension: 50,
        friction: 8,
        useNativeDriver: true,
      }),
      Animated.timing(cardOpacity, {
        toValue: 1,
        duration: 400,
        useNativeDriver: true,
      }),
    ]).start();
  }, []);

  const handleAdminLogin = async () => {
    if (!adminEmail.trim() || !adminPassword.trim()) {
      setAdminLoginError('Please enter both email and password');
      return;
    }

    setAdminLoginError('');
    setAdminLoginLoading(true);

    try {
      const result = await authService.login(adminEmail.trim(), adminPassword);
      
      if (result.error) {
        setAdminLoginError(result.error);
      } else if (result.user) {
        if (result.user.userType === 'admin') {
          navigateTo('MyDashboard');
        } else {
          setAdminLoginError('This account does not have admin access');
          await authService.logout();
        }
      }
    } catch (err) {
      setAdminLoginError('An unexpected error occurred. Please try again.');
      console.error('Admin login error:', err);
    } finally {
      setAdminLoginLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const { id, error: saveError } = await authService.saveEmailCapture(email.trim());
      
      if (saveError) {
        setError(saveError);
      } else {
        setSuccess(true);
        setEmail('');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      console.error('Email capture error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ScrollView 
      style={styles.scrollView}
      contentContainerStyle={styles.scrollContent}
      showsVerticalScrollIndicator={false}
    >
      <View style={styles.container}>
        <Animated.View style={[
          styles.mainCard,
          {
            opacity: cardOpacity,
            transform: [{ scale: cardScale }],
          }
        ]}>
          {/* Character Image */}
          <Image
            source={characterWaving}
            style={styles.characterImage}
            resizeMode="contain"
          />

          {/* Badge */}
          <View style={styles.badge}>
            <Text style={styles.badgeText}>LAUNCHING SOON</Text>
          </View>

          {/* Title */}
          <Text style={styles.title}>
            Something Big Is Coming
          </Text>
          
          <Text style={styles.description}>
            We're putting the finishing touches on something special. 
            Get ready for the Twelve Month Millionaire experience.
          </Text>

          {/* Email Signup Section */}
          <View style={styles.emailCard}>
            <Text style={styles.emailTitle}>
              Be the First to Know
            </Text>
            <Text style={styles.emailSubtitle}>
              Enter your email to get notified when we launch
            </Text>

            <View style={styles.inputRow}>
              <FormInput
                placeholder="Enter your email"
                value={email}
                onChangeText={(text) => {
                  setEmail(text);
                  setError('');
                  setSuccess(false);
                }}
                keyboardType="email-address"
                autoCapitalize="none"
                editable={!loading && !success}
                onSubmit={handleSubmit}
                returnKeyType="done"
                style={styles.emailInput}
              />

              <PrimaryButton
                title={loading ? 'Adding...' : success ? 'Added!' : 'Notify Me'}
                onPress={handleSubmit}
                loading={loading}
                disabled={loading || success}
                style={styles.submitButton}
              />
            </View>

            {error ? (
              <Text style={styles.errorText}>{error}</Text>
            ) : null}

            {success ? (
              <Text style={styles.successText}>
                ✓ You're on the list! We'll notify you when we launch.
              </Text>
            ) : null}
          </View>
        </Animated.View>

        {/* Admin Login Section */}
        <View style={styles.adminSection}>
          {!showAdminLogin ? (
            <TouchableOpacity
              style={styles.adminLink}
              onPress={() => setShowAdminLogin(true)}
            >
              <Text style={styles.adminLinkText}>
                Admin Access
              </Text>
            </TouchableOpacity>
          ) : (
            <View style={styles.adminLoginCard}>
              <Text style={styles.adminLoginTitle}>
                Admin Login
              </Text>
              <Text style={styles.adminLoginSubtitle}>
                Sign in with admin credentials to bypass
              </Text>
              
              <FormInput
                placeholder="Admin email"
                value={adminEmail}
                onChangeText={(text) => {
                  setAdminEmail(text);
                  setAdminLoginError('');
                }}
                keyboardType="email-address"
                autoCapitalize="none"
                editable={!adminLoginLoading}
                style={styles.adminInput}
              />
              
              <FormInput
                placeholder="Password"
                value={adminPassword}
                onChangeText={(text) => {
                  setAdminPassword(text);
                  setAdminLoginError('');
                }}
                secureTextEntry
                editable={!adminLoginLoading}
                onSubmit={handleAdminLogin}
                style={styles.adminInput}
              />

              {adminLoginError ? (
                <Text style={styles.adminErrorText}>{adminLoginError}</Text>
              ) : null}

              <View style={styles.adminButtonRow}>
                <TouchableOpacity
                  style={styles.adminCancelButton}
                  onPress={() => {
                    setShowAdminLogin(false);
                    setAdminEmail('');
                    setAdminPassword('');
                    setAdminLoginError('');
                  }}
                >
                  <Text style={styles.adminCancelText}>Cancel</Text>
                </TouchableOpacity>
                
                <PrimaryButton
                  title={adminLoginLoading ? 'Signing in...' : 'Sign In'}
                  onPress={handleAdminLogin}
                  loading={adminLoginLoading}
                  disabled={adminLoginLoading}
                  style={styles.adminLoginButton}
                />
              </View>
            </View>
          )}
        </View>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    paddingVertical: 60,
  },
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  mainCard: {
    width: '100%',
    maxWidth: 550,
    backgroundColor: '#FF4747',
    borderRadius: 24,
    borderWidth: 4,
    borderColor: '#000000',
    padding: 40,
    alignItems: 'center',
    ...Platform.select({
      web: {
        boxShadow: '8px 10px 0px rgba(0,0,0,0.9)',
      },
    }),
  },
  characterImage: {
    width: 180,
    height: 180,
    marginBottom: 16,
  },
  badge: {
    backgroundColor: '#FFE66D',
    paddingHorizontal: 20,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 3,
    borderColor: '#000000',
    marginBottom: 16,
    ...Platform.select({
      web: {
        boxShadow: '3px 3px 0px rgba(0,0,0,0.9)',
      },
    }),
  },
  badgeText: {
    fontSize: 12,
    fontFamily: 'Archivo',
    fontWeight: '700',
    letterSpacing: 2,
    color: '#000000',
    textTransform: 'uppercase',
  },
  title: {
    fontSize: 36,
    fontFamily: 'Chivo',
    fontWeight: '900',
    textAlign: 'center',
    marginBottom: 16,
    lineHeight: 42,
    color: '#FFFFFF',
    ...Platform.select({
      web: {
        textShadow: '2px 2px 0px rgba(0,0,0,0.5)',
      },
    }),
  },
  description: {
    fontSize: 16,
    fontFamily: 'Archivo',
    textAlign: 'center',
    marginBottom: 32,
    lineHeight: 24,
    color: 'rgba(255,255,255,0.9)',
    maxWidth: 420,
  },
  emailCard: {
    width: '100%',
    backgroundColor: '#FFFFFF',
    padding: 24,
    borderRadius: 12,
    borderWidth: 4,
    borderColor: '#000000',
    alignItems: 'center',
    ...Platform.select({
      web: {
        boxShadow: '5px 5px 0px rgba(0,0,0,0.9)',
      },
    }),
  },
  emailTitle: {
    fontSize: 20,
    fontFamily: 'Chivo',
    fontWeight: '700',
    marginBottom: 8,
    textAlign: 'center',
    color: '#000000',
  },
  emailSubtitle: {
    fontSize: 14,
    fontFamily: 'Archivo',
    textAlign: 'center',
    marginBottom: 20,
    lineHeight: 20,
    color: 'rgba(0,0,0,0.7)',
  },
  inputRow: {
    flexDirection: 'row',
    width: '100%',
    gap: 12,
    alignItems: 'center',
  },
  emailInput: {
    flex: 1,
    marginBottom: 0,
  },
  submitButton: {
    marginTop: 0,
    minWidth: 120,
  },
  errorText: {
    fontSize: 14,
    fontFamily: 'Archivo',
    color: '#DC2626',
    textAlign: 'center',
    marginTop: 12,
  },
  successText: {
    fontSize: 14,
    fontFamily: 'Archivo',
    textAlign: 'center',
    marginTop: 12,
    fontWeight: '600',
    color: '#16A34A',
  },
  // Admin section
  adminSection: {
    marginTop: 32,
    alignItems: 'center',
    width: '100%',
    maxWidth: 550,
  },
  adminLink: {
    padding: 12,
  },
  adminLinkText: {
    fontSize: 14,
    fontFamily: 'Archivo',
    color: 'rgba(0,0,0,0.6)',
    textDecorationLine: 'underline',
  },
  adminLoginCard: {
    width: '100%',
    padding: 24,
    borderRadius: 12,
    borderWidth: 4,
    borderColor: '#000000',
    backgroundColor: '#4ECDC4',
    gap: 16,
    ...Platform.select({
      web: {
        boxShadow: '6px 8px 0px rgba(0,0,0,0.9)',
      },
    }),
  },
  adminLoginTitle: {
    fontSize: 22,
    fontFamily: 'Chivo',
    fontWeight: '700',
    textAlign: 'center',
    color: '#000000',
  },
  adminLoginSubtitle: {
    fontSize: 14,
    fontFamily: 'Archivo',
    textAlign: 'center',
    marginBottom: 8,
    color: 'rgba(0,0,0,0.7)',
  },
  adminInput: {
    marginBottom: 0,
  },
  adminErrorText: {
    fontSize: 14,
    fontFamily: 'Archivo',
    color: '#FFFFFF',
    backgroundColor: '#DC2626',
    textAlign: 'center',
    padding: 8,
    borderRadius: 8,
  },
  adminButtonRow: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 8,
  },
  adminCancelButton: {
    flex: 1,
    padding: 14,
    borderRadius: 8,
    borderWidth: 3,
    borderColor: '#000000',
    backgroundColor: '#FFFFFF',
    alignItems: 'center',
    ...Platform.select({
      web: {
        cursor: 'pointer',
        boxShadow: '3px 3px 0px rgba(0,0,0,0.9)',
      },
    }),
  },
  adminCancelText: {
    fontSize: 15,
    fontFamily: 'Archivo',
    fontWeight: '600',
    color: '#000000',
  },
  adminLoginButton: {
    flex: 1,
    marginTop: 0,
    paddingVertical: 14,
    paddingHorizontal: 24,
  },
});

export default ComingSoonScreen;
